'use client';

import { useEffect, useId, useState, type ReactNode } from 'react';
import { IconArrowLeft } from '@/components/icons/IconArrowLeft';
import { IconArrowRight } from '@/components/icons/IconArrowRight';
import { IconCheck } from '@/components/icons/IconCheck';
import { IconPlus } from '@/components/icons/IconPlus';
import { FormSpamGuard } from '@/features/forms/components/FormSpamGuard';
import { useFillDuration } from '@/features/forms/useFillDuration';
import { detectSpam } from '@/features/forms/spam-guard';
import { SUBJECT_FIELD, WAY_FIELD } from '@/features/forms/form.types';
import { EMAIL_INPUT_PATTERN, EMAIL_INPUT_TITLE } from '@/lib/utils/email';
import { PHONE_INPUT_PATTERN, PHONE_INPUT_TITLE } from '@/lib/utils/phone';
import { cn } from '@/lib/utils/tw-merge';
import { contactHints } from './contact-validation';
import type { ContactModuleContent, ContactQuizOption, ContactQuizStep } from './ContactModule.types';

type ContactModuleProps = {
    content: ContactModuleContent;
    /** Inside the route modal the module drops its own intro — the modal has one. */
    compact?: boolean;
};

type Status = 'idle' | 'sending' | 'sent' | 'error';

/** How long a sent column stays on its confirmation before returning to the
 *  form. The bar under the confirmation runs for exactly this long, so the
 *  reader can see the reset coming rather than have the panel change under them. */
const CONFIRMATION_MS = 5000;

/** The fade between two questions. Short enough to read as one motion, long
 *  enough that the answer is not replaced under the cursor. */
const STEP_FADE_MS = 220;

/** A typed answer is kept per step and answer: on a question with several
 *  answers, two free-text fields can be open at once. */
const textKey = (index: number, option: ContactQuizOption) => `${index}:${option._key}`;

/**
 * Two ways into a conversation, side by side and both open: leave a number, or
 * answer the questions first so the call that follows starts further along.
 *
 * Neither way is a door into a second screen — the questions run inside their
 * own column, one at a time, and the other column stays reachable throughout.
 * That is also why each column carries its own status: a sent callback confirms
 * in place without taking the questions away.
 */
export function ContactModule({ content, compact = false }: ContactModuleProps) {
    const [stepIndex, setStepIndex] = useState(0);
    // The picked answers per step, by key — one on an ordinary question, any
    // number where the question allows several.
    const [answers, setAnswers] = useState<Record<number, string[]>>({});
    const [freeText, setFreeText] = useState<Record<string, string>>({});
    const [isFading, setIsFading] = useState(false);
    const [callbackStatus, setCallbackStatus] = useState<Status>('idle');
    const [quizStatus, setQuizStatus] = useState<Status>('idle');
    // Phone and email are both optional and either one is enough, so neither
    // field can carry `required` and the send has to be gated on the pair. Kept
    // per column: the two forms are filled independently, and a number typed
    // into one must not unlock the other's button.
    const [callbackContact, setCallbackContact] = useState({ phone: '', email: '' });
    const [quizContact, setQuizContact] = useState({ phone: '', email: '' });
    const stampFillDuration = useFillDuration();
    const freeTextId = useId();
    const callbackHintId = useId();
    const quizHintId = useId();

    // What still keeps each column's send shut; empty once it may go.
    const callbackHints = contactHints(callbackContact);
    const quizHints = contactHints(quizContact);

    const steps = content.quiz.steps ?? [];
    const isPastLastStep = stepIndex >= steps.length;
    const step = steps[stepIndex];
    const isMultiple = Boolean(step?.multiple);
    const picked = answers[stepIndex] ?? [];
    // Every picked free-text answer opens its own field.
    const openTexts = step?.options.filter((option) => option.isFreeText && picked.includes(option._key)) ?? [];
    // Answered once something is picked and each open field says what it means.
    const isAnswered = picked.length > 0 && openTexts.every((option) => freeText[textKey(stepIndex, option)]?.trim());

    const resetQuiz = () => {
        setStepIndex(0);
        setAnswers({});
        setFreeText({});
        setQuizContact({ phone: '', email: '' });
        setQuizStatus('idle');
    };

    /* The contact pair is controlled state now, so it outlives the form's
       unmount — a sent column coming back off its confirmation has to be
       emptied explicitly or it would return holding the address it just used,
       with the send already unlocked. */
    const resetCallback = () => {
        setCallbackContact({ phone: '', email: '' });
        setCallbackStatus('idle');
    };

    /* Every step change goes through here so the fade is the same whichever
       direction the reader moves, and so a second click during the fade is
       ignored rather than skipping a question. */
    const moveTo = (next: number) => {
        if (isFading) return;
        setIsFading(true);
        window.setTimeout(() => {
            setStepIndex(next);
            setIsFading(false);
        }, STEP_FADE_MS);
    };

    const setText = (option: ContactQuizOption, text: string) =>
        setFreeText((previous) => ({ ...previous, [textKey(stepIndex, option)]: text }));

    const focusText = (option: ContactQuizOption) =>
        window.setTimeout(() => document.getElementById(`${freeTextId}-${option._key}`)?.focus(), 30);

    const chooseOption = (option: ContactQuizOption) => {
        if (isFading) return;
        const isPicked = picked.includes(option._key);

        // Where several answers may stand, a click only turns one on or off and
        // „weiter“ moves on. Turning a free-text answer off discards its text.
        if (isMultiple) {
            const next = isPicked ? picked.filter((key) => key !== option._key) : [...picked, option._key];
            setAnswers((previous) => ({ ...previous, [stepIndex]: next }));

            if (option.isFreeText) {
                if (isPicked) setText(option, '');
                else focusText(option);
            }
            return;
        }

        // Switching away from a free-text answer discards what was typed.
        openTexts.filter((open) => open._key !== option._key).forEach((open) => setText(open, ''));
        setAnswers((previous) => ({ ...previous, [stepIndex]: [option._key] }));

        // A free-text answer needs the field filled before it can move on, so it
        // stays selected and reveals the field; every other answer is complete
        // the moment it is clicked.
        if (option.isFreeText) {
            focusText(option);
            return;
        }

        moveTo(stepIndex + 1);
    };

    // Confirms a free-text answer, or closes a question with several answers.
    const confirmStep = () => {
        if (isAnswered) moveTo(stepIndex + 1);
    };

    // A step's answer as the mail shows it: the picked answers in their order
    // on the page, a free-text one by what was typed into it.
    const answerText = (quizStep: ContactQuizStep, index: number) =>
        quizStep.options
            .filter((option) => answers[index]?.includes(option._key))
            .map((option) => (option.isFreeText && freeText[textKey(index, option)]?.trim()) || option.label)
            .join(', ');

    const handleSubmit = (setStatus: (status: Status) => void) => async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setStatus('sending');

        try {
            const formElement = event.currentTarget;
            const formData = new FormData(formElement);
            stampFillDuration(formData);

            // The route checks the same signals again — a bot can post directly.
            if (detectSpam(formData)) {
                setStatus('error');
                return;
            }

            const response = await fetch('/api/send-form', { method: 'POST', body: formData });
            setStatus(response.ok ? 'sent' : 'error');
        } catch {
            setStatus('error');
        }
    };

    const contactFields = (
        status: Status,
        contact: { phone: string; email: string },
        setContact: (next: { phone: string; email: string }) => void,
    ) => (
        <div className="contact-module__fields">
            <label className="contact-module__field">
                <span className="contact-module__label">
                    {content.phoneLabel}
                    {content.optionalNote ? (
                        <span className="contact-module__label-note"> {content.optionalNote}</span>
                    ) : null}
                </span>
                <input
                    className="form-input"
                    type="tel"
                    name={content.phoneLabel}
                    autoComplete="tel"
                    pattern={PHONE_INPUT_PATTERN}
                    title={PHONE_INPUT_TITLE}
                    placeholder={content.phonePlaceholder}
                    value={contact.phone}
                    onChange={(event) => setContact({ ...contact, phone: event.target.value })}
                />
            </label>

            <label className="contact-module__field">
                <span className="contact-module__label">
                    {content.emailLabel}
                    {content.optionalNote ? (
                        <span className="contact-module__label-note"> {content.optionalNote}</span>
                    ) : null}
                </span>
                <input
                    className="form-input"
                    type="email"
                    name={content.emailLabel}
                    autoComplete="email"
                    pattern={EMAIL_INPUT_PATTERN}
                    title={EMAIL_INPUT_TITLE}
                    placeholder={content.emailPlaceholder}
                    value={contact.email}
                    onChange={(event) => setContact({ ...contact, email: event.target.value })}
                />
            </label>

            {content.contactHint ? <p className="contact-module__hint">{content.contactHint}</p> : null}

            {status === 'error' && content.success.errorMessage ? (
                <p className="contact-module__error" role="alert">
                    {content.success.errorMessage}
                </p>
            ) : null}
        </div>
    );

    // The fallback keeps the button named should the studio leave its label empty.
    const continueButton = (
        <button
            type="button"
            className="btn btn-primary contact-module__continue"
            disabled={!isAnswered}
            onClick={confirmStep}
        >
            {content.quiz.continueLabel || 'Weiter'}
            <IconArrowRight />
        </button>
    );

    return (
        <div className={cn('contact-module', compact && 'contact-module--compact')}>
            {!compact ? (
                <div className="contact-module__intro">
                    {content.eyebrow ? (
                        <span className="eyebrow">
                            <span className="eyebrow__mark" aria-hidden="true" />
                            <span>{content.eyebrow}</span>
                        </span>
                    ) : null}

                    <h2 className="contact-module__title">{content.title}</h2>
                    {content.description ? <p className="contact-module__description">{content.description}</p> : null}
                </div>
            ) : null}

            <div className="contact-module__paths">
                {/* ── The short way ─────────────────────────────────── */}
                <div className="contact-module__column">
                    {callbackStatus === 'sent' ? (
                        <Confirmation
                            content={content}
                            message={content.callback.successMessage}
                            onDone={resetCallback}
                        />
                    ) : (
                        <form className="contact-module__form" onSubmit={handleSubmit(setCallbackStatus)}>
                            <input type="hidden" name={SUBJECT_FIELD} defaultValue={content.callback.title} />
                            <input type="hidden" name={WAY_FIELD} value="callback" readOnly />
                            <FormSpamGuard />

                            <ColumnHead
                                eyebrow={content.callback.eyebrow}
                                title={content.callback.title}
                                description={content.callback.description}
                            />

                            {contactFields(callbackStatus, callbackContact, setCallbackContact)}

                            <SubmitRow hints={callbackHints} hintId={callbackHintId}>
                                <button
                                    type="submit"
                                    className="btn btn-primary contact-module__submit"
                                    disabled={callbackStatus === 'sending' || callbackHints.length > 0}
                                    aria-describedby={callbackHints.length ? callbackHintId : undefined}
                                >
                                    {content.callback.submitLabel}
                                </button>
                            </SubmitRow>
                        </form>
                    )}
                </div>

                {/* ── The longer way ────────────────────────────────── */}
                <div className="contact-module__column contact-module__column--questions">
                    {quizStatus === 'sent' ? (
                        <Confirmation content={content} message={content.quiz.successMessage} onDone={resetQuiz} />
                    ) : (
                        <form className="contact-module__form" onSubmit={handleSubmit(setQuizStatus)}>
                            <input type="hidden" name={SUBJECT_FIELD} defaultValue={content.quiz.title} />
                            <input type="hidden" name={WAY_FIELD} value="quiz" readOnly />
                            <FormSpamGuard />

                            {/* Every answered step travels with the submission, so the
                                reply can start from what was already said. */}
                            {steps.map((quizStep, index) => {
                                const answer = answerText(quizStep, index);

                                return answer ? (
                                    <input
                                        key={quizStep._key}
                                        type="hidden"
                                        name={quizStep.question}
                                        value={answer}
                                        readOnly
                                    />
                                ) : null;
                            })}

                            {isPastLastStep ? (
                                <>
                                    <ColumnHead
                                        eyebrow={content.quiz.finalEyebrow ?? content.quiz.eyebrow}
                                        title={content.quiz.finalTitle ?? content.quiz.title}
                                        description={content.quiz.finalDescription}
                                    />

                                    {contactFields(quizStatus, quizContact, setQuizContact)}

                                    <div className="contact-module__final-row">
                                        <button
                                            type="button"
                                            className="contact-module__back"
                                            onClick={() => moveTo(steps.length - 1)}
                                        >
                                            <IconArrowLeft />
                                            {content.quiz.backLabel}
                                        </button>

                                        <SubmitRow hints={quizHints} hintId={quizHintId}>
                                            <button
                                                type="submit"
                                                className="btn btn-primary contact-module__submit"
                                                disabled={quizStatus === 'sending' || quizHints.length > 0}
                                                aria-describedby={quizHints.length ? quizHintId : undefined}
                                            >
                                                {content.quiz.submitLabel}
                                            </button>
                                        </SubmitRow>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <ColumnHead
                                        eyebrow={content.quiz.eyebrow}
                                        title={content.quiz.title}
                                        description={content.quiz.description}
                                    />

                                    {step ? (
                                        <div className={cn('contact-module__quiz', isFading && 'is-fading')}>
                                            <h4 className="contact-module__question">{step.question}</h4>
                                            {isMultiple && content.quiz.multipleNote ? (
                                                <p className="contact-module__question-note">
                                                    {content.quiz.multipleNote}
                                                </p>
                                            ) : null}

                                            <ul className="contact-module__options">
                                                {step.options.map((option) => {
                                                    const isSelected = picked.includes(option._key);

                                                    return (
                                                        <li key={option._key}>
                                                            <button
                                                                type="button"
                                                                className={cn(
                                                                    'contact-module__option',
                                                                    isSelected && 'is-selected',
                                                                )}
                                                                aria-pressed={isSelected}
                                                                onClick={() => chooseOption(option)}
                                                            >
                                                                <span>{option.label}</span>
                                                                {isMultiple ? (
                                                                    <span
                                                                        className="contact-module__option-mark"
                                                                        aria-hidden="true"
                                                                    >
                                                                        <IconPlus className="contact-module__option-plus" />
                                                                        <IconCheck
                                                                            className="contact-module__option-check"
                                                                            strokeWidth={1.6}
                                                                        />
                                                                    </span>
                                                                ) : (
                                                                    <IconArrowRight
                                                                        className="contact-module__option-arrow"
                                                                        aria-hidden="true"
                                                                    />
                                                                )}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>

                                            {openTexts.map((option) => (
                                                <div key={option._key} className="contact-module__free-text">
                                                    <input
                                                        id={`${freeTextId}-${option._key}`}
                                                        className="form-input"
                                                        type="text"
                                                        aria-label={option.label}
                                                        placeholder={option.placeholder}
                                                        value={freeText[textKey(stepIndex, option)] ?? ''}
                                                        onChange={(event) => setText(option, event.target.value)}
                                                        onKeyDown={(event) => {
                                                            if (event.key !== 'Enter') return;
                                                            event.preventDefault();
                                                            confirmStep();
                                                        }}
                                                    />
                                                    {/* With several answers the one „weiter“ below
                                                        serves every field. */}
                                                    {isMultiple ? null : continueButton}
                                                </div>
                                            ))}

                                            {/* There from the start and shut until the step is
                                                answered, so the way on is visible before it opens. */}
                                            {isMultiple ? (
                                                <div className="contact-module__next">{continueButton}</div>
                                            ) : null}

                                            <div className="contact-module__quiz-foot">
                                                <ol className="contact-module__progress" aria-hidden="true">
                                                    {steps.map((quizStep, index) => (
                                                        <li
                                                            key={quizStep._key}
                                                            className={cn(
                                                                'contact-module__tick',
                                                                index < stepIndex && 'is-done',
                                                                index === stepIndex && 'is-current',
                                                            )}
                                                        />
                                                    ))}
                                                </ol>

                                                <div className="contact-module__quiz-foot-mid">
                                                    {stepIndex > 0 ? (
                                                        <button
                                                            type="button"
                                                            className="contact-module__back"
                                                            onClick={() => moveTo(stepIndex - 1)}
                                                        >
                                                            <IconArrowLeft />
                                                            {content.quiz.backLabel}
                                                        </button>
                                                    ) : null}
                                                </div>

                                                <p className="contact-module__step-count">
                                                    {content.quiz.stepLabel ? `${content.quiz.stepLabel} ` : ''}
                                                    {stepIndex + 1} / {steps.length}
                                                </p>
                                            </div>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

type SubmitRowProps = {
    hints: string[];
    hintId: string;
    children: ReactNode;
};

/** The send and, beside it, why it is still shut — the validation's own
 *  messages, so a greyed-out button never leaves the reader guessing. The
 *  status line stays mounted while empty, so its first message is announced. */
function SubmitRow({ hints, hintId, children }: SubmitRowProps) {
    return (
        <div className="contact-module__submit-row">
            {children}
            <p id={hintId} className="contact-module__submit-hint" role="status">
                {hints.join(' ')}
            </p>
        </div>
    );
}

type ColumnHeadProps = {
    eyebrow?: string;
    title: string;
    description?: string;
};

/** The three lines that open a column. Both columns share them so the pair
 *  reads as two answers to one question rather than as two components. */
function ColumnHead({ eyebrow, title, description }: ColumnHeadProps) {
    return (
        <div className="contact-module__column-head">
            {eyebrow ? (
                <span className="eyebrow contact-module__column-eyebrow">
                    <span className="eyebrow__mark" aria-hidden="true" />
                    <span>{eyebrow}</span>
                </span>
            ) : null}

            <h3 className="contact-module__card-title">{title}</h3>
            {description ? <p className="contact-module__card-text">{description}</p> : null}
        </div>
    );
}

type ConfirmationProps = {
    content: ContactModuleContent;
    message?: string;
    onDone: () => void;
};

/** Confirms inside the column that was sent, and returns it to its form after
 *  CONFIRMATION_MS — the bar along the bottom is that countdown made visible. */
function Confirmation({ content, message, onDone }: ConfirmationProps) {
    useEffect(() => {
        const timer = window.setTimeout(onDone, CONFIRMATION_MS);
        return () => window.clearTimeout(timer);
        // onDone is recreated per render; the countdown must not restart with it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="contact-module__success">
            <span className="contact-module__success-mark" aria-hidden="true">
                <svg viewBox="0 0 96 84" fill="none">
                    <path d="M48 3 93.5 81.5H2.5Z" stroke="currentColor" strokeWidth="2.6" strokeLinejoin="round" />
                    <path
                        d="M35 52.5 44 61.5 62 42.5"
                        stroke="currentColor"
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>

            {content.success.eyebrow ? (
                <span className="eyebrow contact-module__column-eyebrow contact-module__success-eyebrow">
                    <span className="eyebrow__mark" aria-hidden="true" />
                    <span>{content.success.eyebrow}</span>
                </span>
            ) : null}

            <h3 className="contact-module__card-title">{content.success.title}</h3>
            {message ? <p className="contact-module__card-text">{message}</p> : null}

            <button type="button" className="contact-module__back" onClick={onDone}>
                <IconArrowLeft />
                {content.success.closeLabel}
            </button>

            <span className="contact-module__success-bar" aria-hidden="true">
                <span style={{ animationDuration: `${CONFIRMATION_MS}ms` }} />
            </span>
        </div>
    );
}
